import React from "react";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { getErrorInfo } from "../../utils/errorUtils.js";
import Button from "./button.js";

/**
 * ErrorDisplay Component - Displays error messages with retry functionality
 * Matches the design pattern shown in the screenshot
 * 
 * @param {Object} props - Component properties
 * @param {Error} props.error - The error object
 * @param {Function} props.onRetry - Callback function when retry button is clicked
 * @param {string} props.customTitle - Optional custom error title
 * @param {string} props.customMessage - Optional custom error message
 * @param {string} props.customDescription - Optional custom error description
 * @param {string} props.className - Additional CSS classes
 */
const ErrorDisplay = ({
    error,
    onRetry,
    customTitle = null,
    customMessage = null,
    customDescription = null,
    className = ""
}) => {
    const { language } = useLanguage();
    
    // Validate error exists
    if (!error) {
        console.warn("ErrorDisplay: No error provided");
        return null;
    }
    
    // Get error information using utility
    const errorInfo = getErrorInfo(error, language);
    
    // Use custom values if provided, otherwise use errorInfo
    const title = customTitle || errorInfo.title;
    const message = customMessage || errorInfo.message;
    const description = customDescription || errorInfo.description;
    
    return (
        <div className={`flex items-center justify-center min-h-[400px] px-4 ${className}`}>
            <div className="bg-white dark:bg-[#161b22] rounded-lg border border-red-200 dark:border-red-800 p-6 max-w-md w-full">
                <div className="text-center">
                    <p className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
                        {title}
                    </p>
                    {description && (
                        <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4">
                            {description}
                        </p>
                    )}
                    {message && !description && (
                        <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4">
                            {message}
                        </p>
                    )}
                    {onRetry && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onRetry}
                        >
                            {getTranslation('common.tryAgain', language) || "Կրկին փորձել"}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ErrorDisplay;

